import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
} from "react";

interface FeatureFlags {
  isPricingCalculatorEnabled: boolean;
}

interface FeatureFlagContextType {
  flags: FeatureFlags;
  setFeatureFlags: (flags: Partial<FeatureFlags>) => void;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(
  undefined
);

interface FeatureFlagProviderProps {
  children: ReactNode;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({
  children,
}) => {
  const [flags, setFlags] = useState<FeatureFlags>({
    isPricingCalculatorEnabled: false,
  });

  // Memoize the setFeatureFlags function to prevent unnecessary rerenders
  const setFeatureFlags = useCallback((newFlags: Partial<FeatureFlags>) => {
    setFlags((prev) => ({ ...prev, ...newFlags }));
  }, []);

  // Create a memoized value for the context
  const contextValue = React.useMemo(
    () => ({ flags, setFeatureFlags }),
    [flags, setFeatureFlags]
  );

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlags => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagProvider"
    );
  }
  return context.flags;
};

export const useFeatureFlagUpdater = () => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error(
      "useFeatureFlagUpdater must be used within a FeatureFlagProvider"
    );
  }
  return context.setFeatureFlags;
};
